import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Column, Columns, Container } from '@resurs/midas-react'
import { midas } from '@resurs/midas'
import {
  debitTransactions,
  searchTransactions,
} from '../../store/actions/transactions-actions'
import { getCurrentUserStores } from '../../store/actions/store-info-actions'
import { sendTransactionChangeFilterEvent } from '../../util/ga-helpers'
import { getUsers } from '../../store/actions/users-actions'
import { sendReceiptSummary } from '../../store/actions/receipt-summary-actions'
import { buildCombinedFilters } from './ongoingPaymentUtils'
import OngoingPaymentsUI from './OngoingPaymentsUI'
import { DEFAULT_SEARCH_FILTERS } from './constants'
import { usePagination } from '../../provider/PaginationProvider'
import PageParamBuilder from '../../higher-order/pageable-table/page-param-builder'
import { SearchInfo } from '../../components/common/Table/wrappers/SearchableTableWrapper/searchInfo'
import usePaginationEffect from '../../hooks/usePaginationEffect'

const OngoingPayments = props => {
  const { paginationData, setPaginationData, updateQueryParams } =
    usePagination()
  const hasMountedRef = useRef(false)
  const [isLoading, setLoading] = useState(false)

  useEffect(() => {
    const userPageInfo = Object.assign(new PageParamBuilder(), {
      size: 1000,
    })
    const searchInfo = Object.assign(new SearchInfo(), {
      search: ' ',
    })
    props.getUsers(userPageInfo, searchInfo)
    props.getCurrentUserStores()
  }, [])

  const loadTransactions = (
    newSearchFilters = paginationData?.filters || DEFAULT_SEARCH_FILTERS,
  ) => {
    if (paginationData) {
      setLoading(true)
      props
        .searchTransactions(
          { ...paginationData, sortList: paginationData.sort },
          paginationData.searchQuery,
          buildCombinedFilters(newSearchFilters),
          newSearchFilters,
        )
        .then(data => {
          setPaginationData({
            ...paginationData,
            ...data.page,
            page: data.page.number,
            results: data.results,
          })
        })
        .finally(() => setLoading(false))
    }
  }

  const {
    handleNext,
    handlePrev,
    handleSizeChange,
    handleSearch,
    handleSortChange,
    updatePaginationData,
  } = usePaginationEffect(
    paginationData,
    updateQueryParams,
    loadTransactions,
    hasMounted => {
      if (hasMounted) {
        hasMountedRef.current = hasMounted
      }
    },
    {
      page: 0,
      size: 10,
      sort: 'created,desc',
    },
  )
  const handleStatusFilterChange = newStatus => {
    updatePaginationData(
      'filters',
      {
        ...paginationData.filters,
        status: newStatus,
      },
      true,
    )

    sendTransactionChangeFilterEvent(`${newStatus}`)
  }

  const handleStoreIdFilterChange = newStoreId =>
    updatePaginationData(
      'filters',
      {
        ...paginationData.filters,
        storeId: newStoreId,
      },
      true,
    )

  const doDebitTransactions = (invoices, otherTransactions) => {
    const transactionsToDebit = invoices.concat(otherTransactions)
    props
      .debitTransactions(transactionsToDebit, props.hasInvoiceNumber)
      .then(() => {
        transactionsToDebit.forEach(transaction => {
          props.sendReceiptSummary(transaction.id, 1, {
            email: transaction.customer && transaction.customer.email,
          })
        })
      })
  }

  return (
    <Container>
      <Columns>
        <Column col12>
          {paginationData && props.stores ? (
            <OngoingPaymentsUI
              loading={isLoading}
              users={props.users}
              stores={props.stores}
              debit={doDebitTransactions}
              onLoadSearchData={handleSearch}
              onStatusFilter={handleStatusFilterChange}
              onStoreFilter={handleStoreIdFilterChange}
              role={props.role}
              debitLoading={props.debitLoading}
              loadTransactions={loadTransactions}
              hasInvoiceNumber={props.hasInvoiceNumber}
              debitDisabled={props.debitDisabled}
              onNext={handleNext}
              onPrev={handlePrev}
              onChangeSize={handleSizeChange}
              onChangeSortPriority={handleSortChange}
              delayedPaymentAutomaticDebitDateEnabled={
                props.delayedPaymentAutomaticDebitDateEnabled
              }
              nextRoute={props.nextRoute}
              chainType={props.currentStore?.chainType}
            />
          ) : (
            <div
              className={midas.container}
              style={{
                width: '100%',
                height: '100%',
                zIndex: 10001,
                position: 'fixed',
                top: '25%',
                left: '50%',
              }}
            >
              <span className={midas.loading} />
            </div>
          )}
        </Column>
      </Columns>
    </Container>
  )
}

OngoingPayments.propTypes = {
  users: PropTypes.object.isRequired,
  stores: PropTypes.array.isRequired,
  searchTransactions: PropTypes.func.isRequired,
  debitTransactions: PropTypes.func.isRequired,
  role: PropTypes.string.isRequired,
  hasInvoiceNumber: PropTypes.bool.isRequired,
  debitLoading: PropTypes.bool.isRequired,
  delayedPaymentAutomaticDebitDateEnabled: PropTypes.bool.isRequired,
}

const mapStateToProps = state => ({
  role: state.auth.role,
  hasInvoiceNumber: state.auth.hasInvoiceNumber,
  debitLoading: state.transactions.debitLoading,
  users: state.users,
  stores: state.storeInfo.stores,
  delayedPaymentAutomaticDebitDateEnabled:
    state.storeInfo.currentRepresentative
      .delayedPaymentAutomaticDebitDateEnabled,
  currentStore: state.storeInfo.currentStore,
})

const mapDispatchToProps = dispatch => ({
  searchTransactions: (
    pageInfo,
    searchTerm,
    combinedFilters,
    searchFilters,
  ) => {
    return dispatch(
      searchTransactions(pageInfo, searchTerm, combinedFilters, searchFilters),
    )
  },
  debitTransactions: (transactions, hasInvoiceNumber) =>
    dispatch(debitTransactions(transactions, hasInvoiceNumber)),
  getUsers: (pageInfo, searchInfo, activeInfo) => {
    dispatch(getUsers(pageInfo, searchInfo, activeInfo))
  },
  sendReceiptSummary: (paymentId, index, email) => {
    dispatch(sendReceiptSummary(paymentId, index, email))
  },
  getCurrentUserStores: () => {
    dispatch(getCurrentUserStores())
  },
})

export default connect(mapStateToProps, mapDispatchToProps)(OngoingPayments)
